import React, { useState } from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Box from "@mui/material/Box";
import FileUploadInput from "./FileUploadInput";
import PdfViewer from "./PdfViewer";
import ViewImageModal from "./ViewImageModal";

const GenericFileUpload = ({ index, inputId, file, handleDocumentUpload }) => {
  const [openViewModal, setOpenViewModal] = useState(false);

  // Safely grab the extension or type
  let isPdf = false;
  if (file?.document_front_extension) {
    isPdf = ["pdf"].includes(file.document_front_extension.toLowerCase());
  } else if (file?.url && file?.url.includes("pdf")) {
    isPdf = true;
  }

  // The safest, cleanest way to let browsers handle downloads or navigate to the image for saving
  const handleDownload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!file?.url) return;

    fetch(file.url, { mode: "cors" })
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = (file?.title || "document") + (isPdf ? ".pdf" : ".jpg");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        // If strict backend CORS blocks the download blob stream, gracefully open it native in a new tab where user can Save
        window.open(file.url, "_blank");
      });
  };

  return (
    <>
      <div
        className="form"
        style={{
          boxSizing: "border-box",
          width: "470px",
          height: "250px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          border: "2px dashed lightgray",
          borderRadius: "12px",
          backgroundColor: "rgba(0, 0, 0, 0.02)",
          position: "relative",
          cursor: "pointer",
        }}
      >
        {/* Render Image or PDF Preview */}
        {file?.url && (
            <div 
              style={{ width: "250px", height: "150px", display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden" }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // We open the modal instead of overwriting the file on image click
                if (!isPdf) {
                  setOpenViewModal(true);
                } else {
                  window.open(file.url, "_blank");
                }
              }}
            >
              {isPdf ? (
                <PdfViewer file={file} />
              ) : (
                <img
                  alt="Document Preview"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  src={file.url}
                />
              )}
            </div>
        )}

        {/* If no URL exists, make the whole container trigger upload */}
        {!file?.url && (
          <div 
            style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }} 
            onClick={() => document.getElementById(inputId).click()} 
          />
        )}

        {/* Button Actions */}
        <Box sx={{ display: "flex", gap: "24px", mt: file?.url ? "16px" : "auto", zIndex: 10, alignItems: "center" }}>
          {file?.url ? (
            <>
              <Button
                variant="text"
                sx={{
                  color: "#043A87",
                  fontFamily: "Proxima Nova",
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  padding: 0,
                  "&:hover": { backgroundColor: "transparent" }
                }}
                startIcon={
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '30px',
                    height: '30px',
                    borderRadius: '22.5px',
                    backgroundColor: 'rgba(4, 58, 135, 0.1)'
                  }}>
                    <FileDownloadIcon sx={{ color: '#043A87', fontSize: '18px' }} />
                  </Box>
                }
                onClick={handleDownload}
              >
                Download
              </Button>

              <Button
                variant="text"
                sx={{
                  color: "#009D0F",
                  fontFamily: "Proxima Nova",
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  padding: 0,
                  "&:hover": { backgroundColor: "transparent" }
                }}
                startIcon={
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '30px',
                    height: '30px',
                    borderRadius: '22.5px',
                    backgroundColor: 'rgba(0, 157, 15, 0.1)'
                  }}>
                    <FileUploadIcon sx={{ color: '#009D0F', fontSize: '18px' }} />
                  </Box>
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  document.getElementById(inputId).click();
                }}
              >
                Change
              </Button>
            </>
          ) : (
            <Button
              variant="text"
              sx={{ color: "black", textTransform: "none", fontFamily: "Proxima Nova", fontSize: "16px", fontWeight: 600 }}
              startIcon={<FileUploadIcon />}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(inputId).click();
              }}
            >
              Upload
            </Button>
          )}
        </Box>

        <Typography
          gutterBottom
          variant="p"
          component="span"
          fontSize="small"
          sx={{ mt: "10px", color: "gray" }}
        >
          Maximum 5 MB. Upload .jpeg, .png, or .pdf files.
        </Typography>
      </div>

      <FileUploadInput
        id={inputId}
        onFileChange={(uploadFile) => {
          handleDocumentUpload(uploadFile, index + 1, file?.title ?? "");
        }}
      />

      {file?.url && !isPdf && (
        <ViewImageModal 
          url={file.url} 
          open={openViewModal} 
          onClose={() => setOpenViewModal(false)} 
        />
      )}
    </>
  );
};

export default GenericFileUpload;
